import { format } from "date-fns"
import { GripVertical, Package, X } from "lucide-react"
import type React from "react"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { layoutOffsetX } from "@/lib/pointerCoords"
import { resolveRouteKind } from "@/lib/routeCostCalculation"
import { getRouteKindStopClasses } from "@/lib/routeKindDisplay"
import { getSlotTypeBlockClasses, getSlotTypeEmoji } from "@/lib/slotTypeDisplay"
import { cn } from "@/lib/utils"
import type { PlannedRoute, PlannedStop } from "@/types/collections"

import type { DragType } from "./types"

/** Planned stops lack these; the block prefers an actual volume or DB stop type when present. */
type TimelineStop = PlannedStop & {
  volume_actual?: number
  volumeActual?: number
  stop_type?: string
}

export function PlanningTimelineStop({
  route,
  stop,
  startPos,
  width,
  isDragging,
  readOnly,
  getVolumeForecastForStop,
  onStopDragStart,
  onDeleteStop,
  onUpdateStopMoney,
}: {
  route: PlannedRoute
  stop: TimelineStop
  startPos: number
  width: number
  isDragging: boolean
  readOnly: boolean
  getVolumeForecastForStop: (route: PlannedRoute, stop: { locationId?: string }) => number
  onStopDragStart: (
    e: React.MouseEvent,
    stop: PlannedStop,
    route: PlannedRoute,
    type: DragType,
  ) => void
  onDeleteStop: (routeId: string, stopId: string) => void
  onUpdateStopMoney: (
    routeId: string,
    stopId: string,
    field: "haulageDeliveryRevenueGbp" | "directInjectionCostGbp",
    value: number | undefined,
  ) => void
}) {
  const volume = stop.volume_actual ?? stop.volumeActual ?? getVolumeForecastForStop(route, stop)
  const hasActual = stop.volume_actual != null || stop.volumeActual != null

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "absolute top-1.5 bottom-1.5 rounded border border-palette-jungleGreen200 border-l-4 transition-all shadow-sm z-10 group",
            // Standby replaces the slot-type style: twMerge can't resolve a plain class against bg-*.
            resolveRouteKind(route.route_kind) === "standby"
              ? getRouteKindStopClasses("standby")
              : getSlotTypeBlockClasses(stop.slotType),
            "hover:z-20 hover:ring-2 hover:ring-blue-400 hover:ring-offset-1",
            !readOnly && "cursor-grab",
            isDragging &&
              "z-30 scale-105 shadow-lg ring-2 ring-blue-500 cursor-grabbing",
          )}
          style={{
            left: `${startPos}px`,
            width: `${width}px`,
            minWidth: "30px",
          }}
          onMouseDown={(e) => {
            e.stopPropagation()
            if (readOnly) return
            const target = e.currentTarget
            const relativeX = layoutOffsetX(target, e.clientX)
            const stopWidth = target.offsetWidth
            const handleWidth = 8

            if (relativeX <= handleWidth) {
              onStopDragStart(e, stop, route, "resize-left")
            } else if (relativeX >= stopWidth - handleWidth) {
              onStopDragStart(e, stop, route, "resize-right")
            } else {
              onStopDragStart(e, stop, route, "move")
            }
          }}
        >
          {!readOnly && (
            <>
              <div
                className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity bg-blue-500/20 rounded-l z-10"
                onMouseDown={(e) => {
                  e.stopPropagation()
                  onStopDragStart(e, stop, route, "resize-left")
                }}
              />

              <div
                className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity bg-blue-500/20 rounded-r z-10"
                onMouseDown={(e) => {
                  e.stopPropagation()
                  onStopDragStart(e, stop, route, "resize-right")
                }}
              />
            </>
          )}
          <div className="h-full p-1 flex flex-col justify-between overflow-hidden">
            <div className="flex items-center gap-0.5">
              <span className="text-xs">
                {getSlotTypeEmoji(stop.slotType)}
              </span>
              {width > 50 && (
                <span className="text-[9px] text-palette-jungleGreen900 font-medium truncate">
                  {stop.name}
                </span>
              )}
            </div>

            {width > 40 && (
              <div className="text-[8px] text-palette-jungleGreen500">
                {format(stop.plannedArrival, "HH:mm")} -{" "}
                {format(stop.plannedDeparture, "HH:mm")}
              </div>
            )}

            {width > 60 && (
              <div className="flex items-center gap-1 text-[8px] text-palette-jungleGreen500">
                <Package size={8} />
                <span>{volume.toLocaleString()}</span>
                {hasActual && (
                  <span className="text-emerald-600 font-medium">
                    (actual)
                  </span>
                )}
              </div>
            )}
          </div>

          {!readOnly && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteStop(route.id, stop.id)
                    }}
                    className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600 z-20"
                  >
                    <X size={10} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  Delete stop
                </TooltipContent>
              </Tooltip>

              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <div className="bg-white/80 rounded px-1 py-0.5 shadow-sm">
                  <GripVertical
                    size={10}
                    className="text-palette-jungleGreen500"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="bg-white border-palette-jungleGreen200 text-palette-jungleGreen900 max-w-xs p-3 shadow-lg"
      >
        <div className="space-y-2">
          <div className="font-semibold">{stop.name}</div>
          <div className="text-xs text-palette-jungleGreen500">
            {stop.address}
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-palette-jungleGreen400">Arrival:</span>
              <span className="ml-1 text-palette-jungleGreen900">
                {format(stop.plannedArrival, "HH:mm")}
              </span>
            </div>
            <div>
              <span className="text-palette-jungleGreen400">Departure:</span>
              <span className="ml-1 text-palette-jungleGreen900">
                {format(stop.plannedDeparture, "HH:mm")}
              </span>
            </div>
            <div>
              <span className="text-palette-jungleGreen400">Volume:</span>
              <span className="ml-1 text-palette-jungleGreen900">
                {volume.toLocaleString()}{" "}
                items
                {hasActual && (
                  <span className="text-emerald-600 text-[10px] ml-1">
                    (actual)
                  </span>
                )}
              </span>
            </div>
            <div>
              <span className="text-palette-jungleGreen400">Service:</span>
              <span className="ml-1 text-palette-jungleGreen900">
                {stop.serviceTimeMinutes} min
              </span>
            </div>
            {!readOnly &&
              (stop.slotType === "haulage_delivery" || stop.stop_type === "haulage_delivery") && (
              <div className="pt-2 mt-2 border-t border-palette-jungleGreen100">
                <Label className="text-xs text-palette-jungleGreen600">
                  Revenue (£)
                </Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                  value={stop.haulageDeliveryRevenueGbp ?? ""}
                  onChange={(e) => {
                    const v = e.target.value
                    onUpdateStopMoney(
                      route.id,
                      stop.id,
                      "haulageDeliveryRevenueGbp",
                      v === "" ? undefined : Math.max(0, parseFloat(v) || 0),
                    )
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="h-8 mt-1 text-sm bg-white"
                />
              </div>
            )}
            {!readOnly &&
              (stop.slotType === "direct_injection" || stop.stop_type === "direct_injection") && (
              <div className="pt-2 mt-2 border-t border-palette-jungleGreen100">
                <Label className="text-xs text-palette-jungleGreen600">
                  Direct injection cost (£)
                </Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                  value={stop.directInjectionCostGbp ?? ""}
                  onChange={(e) => {
                    const v = e.target.value
                    onUpdateStopMoney(
                      route.id,
                      stop.id,
                      "directInjectionCostGbp",
                      v === "" ? undefined : Math.max(0, parseFloat(v) || 0),
                    )
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="h-8 mt-1 text-sm bg-white"
                />
                <p className="mt-1 text-[10px] text-palette-jungleGreen500">
                  This route is costed at the agreed price, not from lease,
                  charging and wages.
                </p>
              </div>
            )}
          </div>
          {!readOnly && (
            <div className="text-xs text-blue-600 font-medium border-t border-palette-jungleGreen100 pt-2 mt-2">
              💡 Drag to move • Drag edges to resize
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
